import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService, getStorageUrl } from '../services/api';
import authService from '../services/authService';
import SearchBar from './SearchBar';
import useSEO from '../hooks/useSEO';
import { categoryUrl, courseUrl } from '../utils/catalogUrls';
import Breadcrumbs from './catalogo/Breadcrumbs';
import '../styles/Cursos.css';
import '../styles/SearchBar.css';

// Sem `categoria`: catálogo completo (/cursos). Com `categoria`: página da categoria (/cursos/:slug).
const Cursos = ({ categoria = null }) => {
  const [cursos, setCursos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const activeCategory = categoria;
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalCursos, setTotalCursos] = useState(0);
  const CURSOS_PER_PAGE = 12;
  const navigate = useNavigate();

  useSEO(activeCategory ? {
    title: `Cursos de ${activeCategory.nome} Online e Gratuitos | ABRALIBER`,
    description: activeCategory.descricao
      || `Conheça os cursos online gratuitos de ${activeCategory.nome} da ABRALIBER. Estude no seu ritmo e solicite seu certificado digital após a conclusão.`,
    url: categoryUrl(activeCategory),
    keywords: `cursos de ${activeCategory.nome}, ${activeCategory.nome} grátis, curso online gratuito, ABRALIBER`,
  } : {
    title: 'Cursos Online Gratuitos com Certificado | ABRALIBER',
    description: 'Explore os cursos online gratuitos da ABRALIBER em saúde, educação, gestão pública, informática, psicologia e outras áreas. Estude grátis e solicite seu certificado após a conclusão.',
    url: '/cursos',
    keywords: 'ABRALIBER, todos os cursos grátis, curso online gratuito, curso extracurricular, lista de cursos gratuitos, EAD gratuito',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategorias();
  }, []);

  useEffect(() => {
    fetchCursos(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory?.id]);

  const fetchCursos = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const params = { page, per_page: CURSOS_PER_PAGE };
      if (activeCategory) params.categoria = activeCategory.id;
      const response = await apiService.cursos.list(params);
      const data = response.data;
      const cursosData = data.data || [];
      if (reset) {
        setCursos(Array.isArray(cursosData) ? cursosData : []);
      } else {
        setCursos(prev => [...prev, ...(Array.isArray(cursosData) ? cursosData : [])]);
      }
      setCurrentPage(data.current_page || 1);
      setLastPage(data.last_page || 1);
      setTotalCursos(data.total || 0);
    } catch (error) {

    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (currentPage < lastPage && !loadingMore) {
      fetchCursos(currentPage + 1, false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await apiService.categorias.list();
      setCategorias(response.data.data || []);
    } catch (error) {

    }
  };

  // A busca só navega quando o usuário escolhe uma categoria; limpar o campo
  // não deve tirar o visitante da página de categoria em que ele chegou.
  const handleFilter = (selected) => {
    if (selected) navigate(categoryUrl(selected));
  };

  const handleMatriculaGratis = async (curso) => {
    const user = authService.getCurrentUser();
    if (!user) {
      navigate(courseUrl(curso));
    } else {
      const enrolled = await authService.isEnrolled(curso.id);
      if (enrolled) {
        navigate(`/curso/${curso.id}/estudo`);
      } else {
        navigate(courseUrl(curso));
      }
    }
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const formatHours = (hours) => {
    return hours ? `Carga horária: ${hours} horas` : 'Carga horária não informada';
  };

  return (
    <div className="cursos-catalog-container" data-page="cursos" data-component="catalog">
      {/* Hero Section */}
      <section className="cursos-hero-section">
        <div className="">
          <center><h1>{activeCategory ? `Cursos de ${activeCategory.nome} online e gratuitos` : 'Cursos Online Gratuitos'}</h1></center>
          <p className="cursos-hero-subtitle">
            {activeCategory?.descricao || (activeCategory
              ? `Conheça os cursos online gratuitos de ${activeCategory.nome} da ABRALIBER. Estude no seu ritmo, com certificado opcional após a conclusão.`
              : 'Explore nossa biblioteca completa de cursos em diversas áreas do conhecimento. Os cursos são 100% gratuitos, com certificado opcional após a conclusão.')}
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="cursos-metrics-area">
        <div className="cursos-metrics-wrapper">
          <div className="cursos-metric-box">
            <span className="cursos-metric-value">+400</span>
            <span className="cursos-metric-text">Cursos Disponíveis</span>
          </div>
          <div className="cursos-metric-box">
            <span className="cursos-metric-value">100%</span>
            <span className="cursos-metric-text">Gratuito</span>
          </div>
          <div className="cursos-metric-box">
            <span className="cursos-metric-value">24/7</span>
            <span className="cursos-metric-text">Acesso Total</span>
          </div>
          <div className="cursos-metric-box">
            <span className="cursos-metric-value">∞</span>
            <span className="cursos-metric-text">Acesso Vitalício</span>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="cursos-content-area">
        <Breadcrumbs items={[
          { name: 'Início', path: '/' },
          { name: 'Cursos', path: '/cursos' },
          ...(activeCategory ? [{ name: activeCategory.nome, path: categoryUrl(activeCategory) }] : []),
        ]} />
        <div className="cursos-header-section">
          <h2 className="cursos-main-title">{activeCategory ? activeCategory.nome : 'Todos os Cursos'}</h2>
          <p className="cursos-main-subtitle">
            Navegue por nossa coleção completa de cursos e encontre o conteúdo perfeito para sua jornada de aprendizagem.
          </p>
        </div>

        <SearchBar onFilter={handleFilter} />

        {categorias.length > 0 && (
          <div className="category-filter-section">
            <div className="category-filter-header">
              <strong>Categorias</strong>
              <span>{activeCategory?.nome || 'Todos os cursos'}</span>
            </div>
            <div className="category-chips-wrapper" role="group" aria-label="Filtrar cursos por categoria">
              <Link
                to="/cursos"
                className={`category-chip${!activeCategory ? ' active' : ''}`}
                aria-current={!activeCategory ? 'page' : undefined}
              >
                Todos
              </Link>
              {categorias.filter(c => c.cursos_count >= 3 || c.id === activeCategory?.id).map((cat) => (
                <Link
                  key={cat.id}
                  to={activeCategory?.id === cat.id ? '/cursos' : categoryUrl(cat)}
                  className={`category-chip${activeCategory && activeCategory.id === cat.id ? ' active' : ''}`}
                  aria-current={activeCategory?.id === cat.id ? 'page' : undefined}
                >
                  <span className="chip-dot" style={{ background: cat.cor }} />
                  {cat.nome}
                  <span className="chip-count">({cat.cursos_count})</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {cursos.length === 0 && !loading ? (
          <div className="cursos-empty-state">
            <p>{activeCategory ? `Nenhum curso encontrado na categoria "${activeCategory.nome}".` : 'Nenhum curso encontrado. Tente novamente mais tarde.'}</p>
          </div>
        ) : (
          <>
          <ul className="cursos-catalog-grid">
            {cursos.map((curso) => (
              <li key={curso.id}>
                <div className="cursos-item-card">
                  <div className="cursos-thumbnail-wrapper">
                    <img 
                      src={curso.imagem_principal?.image_url || curso.imagem_url || getStorageUrl(curso.image) || '/default-course-image.svg'} 
                      alt={curso.title} 
                      className="cursos-thumbnail-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/default-course-image.svg';
                      }}
                    />
                    <div className="cursos-free-badge">100% Grátis</div>
                  </div>
                  
                  <div className="cursos-card-content">
                    <h2><Link to={courseUrl(curso)} className="cursos-card-title-link">{truncateText(curso.title, 60)}</Link></h2>
                    <p className="cursos-card-description">
                      {truncateText(stripHtml(curso.description), 160)}
                    </p>
                    
                    <div className="cursos-card-metadata">
                      <span className="cursos-duration-info">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {formatHours(curso.hours)}
                      </span>
                      <span className="cursos-free-info">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Gratuito
                      </span>
                    </div>
                    
                    <button 
                      className="cursos-enrollment-btn" 
                      onClick={() => handleMatriculaGratis(curso)}
                      data-analytics-course-id={curso.id}
                      data-analytics-source="course_catalog"
                      aria-label={`Fazer matrícula grátis no curso ${curso.title}`}
                      data-curso-action="matricula"
                      data-button-type="primary"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Matrícula Grátis
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {currentPage < lastPage && (
            <div className="load-more-container">
              <button 
                className="load-more-button" 
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <div className="load-more-spinner"></div>
                    Carregando...
                  </>
                ) : (
                  <>
                    Ver mais cursos
                    <span className="load-more-count">
                      ({cursos.length} de {totalCursos})
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
          </>
        )}

        {loading && (
          <div className="cursos-loading-state">
            <div className="cursos-loading-spinner"></div>
            <p>Carregando cursos...</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default Cursos;
